import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate, formatDateTime } from './utils';
import {
  CLINIC_NAME, CLINIC_ADDRESS, CLINIC_EMAIL,
  BUSINESS_PHONES, BUSINESS_PHONES_INLINE, getClinicianPhone,
} from '../config/clinicContacts';

// Global branding constants
const LOGO_URL = "/logo.png"; // Local logo file to avoid CORS issues

// Helper to load image as base64
const loadImageAsBase64 = async (url) => {
  try {
    // Use absolute URL for local files
    const absoluteUrl = url.startsWith('/') ? window.location.origin + url : url;
    const response = await fetch(absoluteUrl, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo:', error);
    return null;
  }
};

// Add header with logo to PDF
const addHeader = async (doc, title, subtitle = '') => {
  const pageWidth = doc.internal.pageSize.width;
  
  // Try to add logo
  try {
    const logoBase64 = await loadImageAsBase64(LOGO_URL);
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', 15, 8, 25, 25);
    }
  } catch (e) {
    console.warn('Could not load logo for PDF');
  }
  
  // Clinic name and info
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(CLINIC_NAME, 45, 18);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(CLINIC_ADDRESS, 45, 24);
  doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, 45, 29);
  doc.text(`Email: ${CLINIC_EMAIL}`, 45, 34);
  
  // Document title on the right
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(title, pageWidth - 15, 18, { align: 'right' });
  
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, pageWidth - 15, 25, { align: 'right' });
  }
  
  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(15, 40, pageWidth - 15, 40);
  
  return 47; // Return Y position after header
};

// Add footer to all pages
const addFooter = (doc, generatedBy, generatedAt) => {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(128, 128, 128);
    doc.setFont('helvetica', 'normal');
    
    // Top footer line: Business contact
    doc.text(`${CLINIC_NAME} | ${BUSINESS_PHONES_INLINE}`, pageWidth / 2, 280, { align: 'center' });
    
    // Left: Generated info
    doc.text(`Generated by: ${generatedBy || 'System'} | Date: ${formatDateTime(generatedAt) || new Date().toLocaleDateString()}`, 15, 285);
    
    // Center: Confidentiality notice
    doc.text('CONFIDENTIAL - Protected Health Information', pageWidth / 2, 285, { align: 'center' });
    
    // Right: Page number
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, 285, { align: 'right' });
  }
};

/**
 * Generate a formal clinical patient summary PDF
 * Suitable for referrals, insurance, or legal documentation
 */
export const generatePatientSummaryPDF = async (summaryData) => {
  try {
    const { patient, recent_encounters, clinical_notes, generated_at, generated_by, generated_by_phone } = summaryData;
    
    if (!patient) {
      throw new Error('Patient data is missing');
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Add header with logo
    let yPos = await addHeader(doc, 'CLINICAL SUMMARY REPORT', `Patient ID: ${patient.patient_id}`);
    
    // =====================
    // PATIENT DEMOGRAPHICS
    // =====================
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('PATIENT DEMOGRAPHICS', 15, yPos);
    yPos += 8;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${patient.first_name} ${patient.last_name}`, 15, yPos);
    yPos += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    
    // Demographics in two columns
    const col1X = 15;
    const col2X = 110;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Date of Birth:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(patient.date_of_birth) || 'N/A', col1X + 30, yPos);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Gender:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.gender || 'N/A', col2X + 20, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Blood Type:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.blood_type || 'Unknown', col1X + 30, yPos);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Phone:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.phone || 'N/A', col2X + 20, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Email:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.email || 'N/A', col1X + 30, yPos);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Address:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.address || 'N/A', col2X + 20, yPos);
    yPos += 10;
    
    // Divider
    doc.setDrawColor(220, 220, 220);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 8;
    
    // =====================
    // ALLERGIES (Highlighted)
    // =====================
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('ALLERGIES', 15, yPos);
    yPos += 6;
    
    doc.setFontSize(10);
    if (patient.allergies && patient.allergies.length > 0) {
      doc.setTextColor(220, 38, 38); // Red for allergies
      doc.setFont('helvetica', 'bold');
      doc.text(patient.allergies.join(', '), 15, yPos);
    } else {
      doc.setTextColor(34, 139, 34); // Green for NKDA
      doc.setFont('helvetica', 'normal');
      doc.text('No Known Drug Allergies (NKDA)', 15, yPos);
    }
    yPos += 10;
    
    // =====================
    // CURRENT MEDICATIONS
    // =====================
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('CURRENT MEDICATIONS', 15, yPos);
    yPos += 6;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    
    if (patient.medications && patient.medications.length > 0) {
      patient.medications.forEach((med) => {
        doc.text(`• ${med}`, 20, yPos);
        yPos += 5;
      });
    } else {
      doc.text('No current medications documented.', 15, yPos);
      yPos += 5;
    }
    yPos += 5;
    
    // =====================
    // MEDICAL HISTORY / ACTIVE PROBLEMS
    // =====================
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('MEDICAL HISTORY / ACTIVE CONDITIONS', 15, yPos);
    yPos += 6;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    
    if (patient.active_problems && patient.active_problems.length > 0) {
      patient.active_problems.forEach((problem) => {
        doc.text(`• ${problem}`, 20, yPos);
        yPos += 5;
      });
    } else {
      doc.text('No significant medical history documented.', 15, yPos);
      yPos += 5;
    }
    yPos += 8;
    
    // Divider
    doc.setDrawColor(220, 220, 220);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 8;
    
    // =====================
    // CLINICAL NARRATIVE
    // =====================
    if (clinical_notes && clinical_notes.length > 0) {
      // Check if we need a new page
      if (yPos > 180) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('CLINICAL NOTES & ENCOUNTER SUMMARY', 15, yPos);
      yPos += 8;
      
      // Merge notes into narrative form
      clinical_notes.forEach((note, index) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        // Note header with date and type
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        
        const noteDate = formatDateTime(note.created_at) || 'Date not recorded';
        const noteType = (note.note_type || 'Clinical Note').toUpperCase();
        const provider = note.created_by_name || note.approved_by_name || 'Provider';
        
        doc.text(`${noteType} - ${noteDate}`, 15, yPos);
        yPos += 5;
        
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(`Documented by: ${provider}`, 15, yPos);
        yPos += 6;
        
        // Note content
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(10);
        
        const noteContent = note.final_note || note.ai_generated_note || note.content || 'No content available.';
        const lines = doc.splitTextToSize(noteContent, pageWidth - 30);
        
        lines.forEach((line) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 15, yPos);
          yPos += 5;
        });
        
        yPos += 8;
      });
    }
    
    // =====================
    // ENCOUNTER HISTORY TABLE
    // =====================
    if (recent_encounters && recent_encounters.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('ENCOUNTER HISTORY', 15, yPos);
      yPos += 5;
      
      const encounterData = recent_encounters.map(enc => [
        formatDate(enc.created_at) || 'N/A',
        enc.form_name || enc.type || 'Visit',
        enc.submitted_by_name || enc.provider || 'Provider',
        enc.disposition || enc.status || '-'
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Type', 'Provider', 'Disposition']],
        body: encounterData,
        theme: 'striped',
        headStyles: { fillColor: [15, 23, 42], fontSize: 9 },
        styles: { fontSize: 9 },
        margin: { left: 15, right: 15 },
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // =====================
    // ATTENDING PHYSICIAN
    // =====================
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setDrawColor(220, 220, 220);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 10;
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('ATTENDING PHYSICIAN:', 15, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`${generated_by || 'Physician'}, M.D.`, 15, yPos);
    yPos += 5;
    
    // Personal phone number — use provided or look up from clinician mapping
    const attendingPhone = generated_by_phone || getClinicianPhone(generated_by);
    if (attendingPhone) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(`Direct: ${attendingPhone}`, 15, yPos);
      yPos += 4;
    }
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(CLINIC_NAME, 15, yPos);
    yPos += 4;
    doc.text(`Contact: ${BUSINESS_PHONES_INLINE}`, 15, yPos);
    yPos += 15;
    
    // Signature line
    doc.setDrawColor(0, 0, 0);
    doc.line(15, yPos, 80, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.text('Signature', 15, yPos);
    
    doc.line(100, yPos - 4, 165, yPos - 4);
    doc.text('Date', 100, yPos);
    
    // Add footer to all pages
    addFooter(doc, generated_by, generated_at);
    
    // Save
    doc.save(`Clinical_Summary_${patient.patient_id}_${new Date().toISOString().split('T')[0]}.pdf`);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generate a standalone prescription PDF
 */
export const generatePrescriptionPDF = async (prescriptionData) => {
  try {
    const { 
      patient, 
      medications, 
      prescriber, 
      prescriber_title,
      prescriber_phone,
      prescription_number,
      date,
      notes 
    } = prescriptionData;
    
    if (!patient || !medications || medications.length === 0) {
      throw new Error('Patient and medication data are required');
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Generate prescription number if not provided
    const rxNumber = prescription_number || `RX-${Date.now().toString().slice(-8)}`;
    
    // Add header with logo
    let yPos = await addHeader(doc, 'PRESCRIPTION', rxNumber);
    
    // Patient Information Box
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(15, yPos, pageWidth - 30, 30, 3, 3, 'F');
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('PATIENT:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(`${patient.first_name} ${patient.last_name}`, 50, yPos);
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('DOB:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(patient.date_of_birth) || 'N/A', 50, yPos);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Patient ID:', 100, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(patient.patient_id || 'N/A', 130, yPos);
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(date) || formatDate(new Date()), 50, yPos);
    
    yPos += 20;
    
    // Rx Symbol
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Rx', 15, yPos);
    
    yPos += 10;
    
    // Medications Table
    const medTableData = medications.map((med, index) => [
      `${index + 1}. ${med.name}`,
      med.dose || 'As directed',
      med.route || 'Oral',
      med.frequency || 'As directed',
      med.duration || '-',
      med.quantity || '-'
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Medication', 'Dose', 'Route', 'Frequency', 'Duration', 'Quantity']],
      body: medTableData,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42], fontSize: 9 },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 25 },
        2: { cellWidth: 20 },
        3: { cellWidth: 35 },
        4: { cellWidth: 22 },
        5: { cellWidth: 23 },
      },
      margin: { left: 15, right: 15 },
    });
    
    yPos = doc.lastAutoTable.finalY + 8;
    
    // Special Instructions for each medication
    const medsWithInstructions = medications.filter(m => m.instructions);
    if (medsWithInstructions.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Special Instructions (SIG):', 15, yPos);
      yPos += 6;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      medsWithInstructions.forEach((med) => {
        const instructionText = `• ${med.name}: ${med.instructions}`;
        const instructionLines = doc.splitTextToSize(instructionText, pageWidth - 35);
        doc.text(instructionLines, 20, yPos);
        yPos += instructionLines.length * 5;
      });
      yPos += 5;
    }
    
    // Notes
    if (notes) {
      yPos += 5;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Additional Notes:', 15, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const noteLines = doc.splitTextToSize(notes, pageWidth - 30);
      doc.text(noteLines, 15, yPos);
      yPos += noteLines.length * 5 + 10;
    }
    
    // Prescriber Information
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    yPos += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(15, yPos, pageWidth - 15, yPos);
    yPos += 15;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('PRESCRIBING PHYSICIAN:', 15, yPos);
    yPos += 8;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${prescriber || 'Physician'}, ${prescriber_title || 'M.D.'}`, 15, yPos);
    yPos += 5;
    
    // Personal phone number — use provided or look up from clinician mapping
    const prescriberDirectPhone = prescriber_phone || getClinicianPhone(prescriber);
    if (prescriberDirectPhone) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(`Direct: ${prescriberDirectPhone}`, 15, yPos);
      yPos += 4;
    }
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(CLINIC_NAME, 15, yPos);
    yPos += 4;
    doc.text(`Contact: ${BUSINESS_PHONES_INLINE}`, 15, yPos);
    
    // Signature line
    yPos += 20;
    doc.setDrawColor(0, 0, 0);
    doc.line(15, yPos, 90, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Prescriber Signature', 15, yPos);
    
    doc.line(110, yPos - 4, 185, yPos - 4);
    doc.text('Date', 110, yPos);
    
    // Dispensing pharmacy section
    yPos += 20;
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(15, yPos, pageWidth - 30, 25, 3, 3, 'F');
    yPos += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('FOR PHARMACY USE ONLY', 20, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Dispensed by: _________________  Date: _________  Pharmacist: _________________', 20, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('This prescription is valid for 30 days from the date of issue unless otherwise specified.', pageWidth / 2, 280, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
    
    // Save
    const patientName = `${patient.first_name}_${patient.last_name}`.replace(/\s+/g, '_');
    doc.save(`Prescription_${patientName}_${new Date().toISOString().split('T')[0]}.pdf`);
    
  } catch (error) {
    console.error('Error generating prescription PDF:', error);
    throw error;
  }
};

/**
 * Generate an invoice PDF with logo
 */
export const generateInvoicePDF = async (invoice) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Add header with logo
  let yPos = await addHeader(doc, 'INVOICE', invoice.invoice_number);
  
  // Bill To section
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('BILL TO:', 15, yPos);
  yPos += 6;
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text(invoice.patient_name || 'Patient', 15, yPos);
  yPos += 5;
  if (invoice.patient_address) {
    doc.text(invoice.patient_address, 15, yPos);
    yPos += 5;
  }
  
  // Invoice details on the right
  const detailsX = pageWidth - 70;
  let detailsY = yPos - 16;
  
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('Invoice Date:', detailsX, detailsY);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(invoice.created_at) || 'N/A', pageWidth - 15, detailsY, { align: 'right' });
  
  detailsY += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Due Date:', detailsX, detailsY);
  doc.setFont('helvetica', 'normal');
  doc.text(invoice.due_date ? formatDate(invoice.due_date) : 'Upon Receipt', pageWidth - 15, detailsY, { align: 'right' });
  
  detailsY += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Status:', detailsX, detailsY);
  doc.setFont('helvetica', 'normal');
  doc.text((invoice.status || 'pending').toUpperCase(), pageWidth - 15, detailsY, { align: 'right' });
  
  yPos += 15;
  
  // Line items table
  const tableData = (invoice.line_items || []).map(item => [
    item.description || 'Service',
    (item.quantity || 1).toString(),
    `$${(item.unit_price || 0).toFixed(2)}`,
    item.adjustment > 0 ? `-$${item.adjustment.toFixed(2)}` : '-',
    `$${(item.total || 0).toFixed(2)}`
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Description', 'Qty', 'Unit Price', 'Adjustment', 'Total']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [15, 23, 42], fontSize: 9 },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
    },
    margin: { left: 15, right: 15 },
  });
  
  // Totals
  const finalY = doc.lastAutoTable.finalY + 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text('Subtotal:', pageWidth - 70, finalY);
  doc.text(`$${(invoice.subtotal || 0).toFixed(2)}`, pageWidth - 15, finalY, { align: 'right' });
  
  doc.text('Adjustments:', pageWidth - 70, finalY + 7);
  doc.text(`-$${(invoice.total_adjustments || 0).toFixed(2)}`, pageWidth - 15, finalY + 7, { align: 'right' });
  
  doc.setLineWidth(0.5);
  doc.setDrawColor(200, 200, 200);
  doc.line(pageWidth - 70, finalY + 12, pageWidth - 15, finalY + 12);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text('Total Due:', pageWidth - 70, finalY + 20);
  doc.text(`$${(invoice.total || 0).toFixed(2)}`, pageWidth - 15, finalY + 20, { align: 'right' });
  
  // Notes
  if (invoice.notes) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Notes:', 15, finalY + 35);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const noteLines = doc.splitTextToSize(invoice.notes, pageWidth - 30);
    doc.text(noteLines, 15, finalY + 42);
  }
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Thank you for choosing ${CLINIC_NAME}`, pageWidth / 2, 270, { align: 'center' });
  doc.text(`Contact: ${BUSINESS_PHONES_INLINE}`, pageWidth / 2, 275, { align: 'center' });
  doc.text(`Payment can be made via cash, card, or bank transfer`, pageWidth / 2, 280, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  // Save
  doc.save(`Invoice_${invoice.invoice_number}.pdf`);
};

/**
 * Generate an AI Medical Report PDF
 */
export const generateAIReportPDF = async (reportData) => {
  const { 
    patient_name, 
    report_type, 
    specialist_type,
    report_text, 
    generated_by,
    generated_by_phone,
    generated_at 
  } = reportData;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  // Report type labels
  const reportTypeLabels = {
    'medical_transfer': 'Medical Transfer Report',
    'fit_to_fly': 'Fit-to-Fly Certificate',
    'hospital_referral': 'Hospital Referral Letter',
    'specialist_referral': specialist_type ? `Specialist Referral - ${specialist_type}` : 'Specialist Referral Letter',
    'fitness_certificate': 'Medical Fitness Certificate'
  };
  
  const reportTitle = reportTypeLabels[report_type] || 'Medical Report';
  
  // Add header with logo
  let yPos = await addHeader(doc, reportTitle.toUpperCase(), `Patient: ${patient_name}`);
  
  // Process the report text - split into lines and pages
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 30, 30);
  
  const lines = doc.splitTextToSize(report_text, contentWidth);
  const lineHeight = 5;
  const maxY = pageHeight - 40; // Leave space for footer
  
  for (let i = 0; i < lines.length; i++) {
    if (yPos > maxY) {
      // Add footer to current page
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      doc.text('CONFIDENTIAL - Protected Health Information', pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Add new page
      doc.addPage();
      yPos = 20;
      
      // Add mini header on continuation pages
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text(`${reportTitle} - ${patient_name} (continued)`, margin, yPos);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos + 3, pageWidth - margin, yPos + 3);
      yPos = yPos + 12;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
    }
    
    doc.text(lines[i], margin, yPos);
    yPos += lineHeight;
  }
  
  // Add signature section if there's space
  if (yPos < pageHeight - 60) {
    yPos += 20;
    doc.setDrawColor(0, 0, 0);
    doc.line(margin, yPos, margin + 70, yPos);
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text('Physician Signature', margin, yPos + 5);
    
    doc.line(pageWidth - margin - 70, yPos, pageWidth - margin, yPos);
    doc.text('Date', pageWidth - margin - 70, yPos + 5);
  }
  
  // Add footer to last page
  const totalPages = doc.internal.getNumberOfPages();
  const aiReportPhone = generated_by_phone || getClinicianPhone(generated_by);
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    const footerText = aiReportPhone 
      ? `Generated by: ${generated_by || 'System'} | Direct: ${aiReportPhone} | ${formatDateTime(generated_at) || new Date().toLocaleString()}`
      : `Generated by: ${generated_by || 'System'} | ${formatDateTime(generated_at) || new Date().toLocaleString()}`;
    doc.text(footerText, margin, pageHeight - 15);
    doc.text(`Contact: ${BUSINESS_PHONES_INLINE}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
    doc.text('CONFIDENTIAL - Protected Health Information', pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }
  
  // Generate filename
  const sanitizedPatientName = patient_name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const sanitizedReportType = report_type.replace(/_/g, '-');
  const dateStr = new Date().toISOString().split('T')[0];
  
  doc.save(`${sanitizedReportType}_${sanitizedPatientName}_${dateStr}.pdf`);
};

/**
 * Generate Lab Requisition PDF
 */
export const generateLabRequestPDF = async (labRequest, user) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 15;
  
  // Add header
  let yPos = await addHeader(doc, 'LABORATORY REQUISITION', labRequest.reference_number);
  
  // Priority badge
  if (labRequest.priority === 'urgent' || labRequest.priority === 'stat') {
    doc.setFillColor(220, 38, 38);
    doc.roundedRect(pageWidth - 45, 42, 30, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(labRequest.priority.toUpperCase(), pageWidth - 30, 47, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }
  
  yPos += 5;
  
  // Patient Information Section
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 35, 2, 2, 'F');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('PATIENT INFORMATION', margin + 5, yPos + 7);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  
  const col1X = margin + 5;
  const col2X = pageWidth / 2;
  
  doc.text(`Name: ${labRequest.patient_name || 'N/A'}`, col1X, yPos + 15);
  doc.text(`Patient ID: ${labRequest.patient_number || 'N/A'}`, col2X, yPos + 15);
  
  doc.text(`DOB: ${labRequest.patient_dob || 'N/A'}`, col1X, yPos + 22);
  doc.text(`Gender: ${labRequest.patient_gender || 'N/A'}`, col2X, yPos + 22);
  
  doc.text(`Phone: ${labRequest.patient_phone || 'N/A'}`, col1X, yPos + 29);
  doc.text(`Email: ${labRequest.patient_email || 'N/A'}`, col2X, yPos + 29);
  
  yPos += 40;
  
  // Clinical Information Section
  doc.setFillColor(254, 249, 195);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 25, 2, 2, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(133, 77, 14);
  doc.text('CLINICAL INFORMATION', margin + 5, yPos + 7);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(`Diagnosis/Indication: ${labRequest.diagnosis || labRequest.clinical_indication || 'N/A'}`, margin + 5, yPos + 15);
  doc.text(`Ordering Physician: ${labRequest.ordering_physician || 'N/A'}`, margin + 5, yPos + 21);
  const labPhysicianPhone = labRequest.physician_phone || getClinicianPhone(labRequest.ordering_physician);
  doc.text(`Phone: ${labPhysicianPhone || 'N/A'}`, col2X, yPos + 21);
  
  yPos += 30;
  
  // Specimen & Conditions Section
  const conditionsY = yPos;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPos, (pageWidth - margin * 2) / 2 - 5, 30, 2, 2, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text('SPECIMEN DETAILS', margin + 5, yPos + 7);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(`Collection Date: ${labRequest.specimen_collection_date || '___/___/______'}`, margin + 5, yPos + 14);
  doc.text(`Collection Time: ${labRequest.specimen_collection_time || '___:___'}`, margin + 5, yPos + 20);
  doc.text(`Phlebotomist: ${labRequest.phlebotomist || '_________________'}`, margin + 5, yPos + 26);
  
  // Patient Conditions
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(pageWidth / 2 + 2, conditionsY, (pageWidth - margin * 2) / 2 - 5, 30, 2, 2, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text('PATIENT CONDITIONS', pageWidth / 2 + 7, conditionsY + 7);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  
  const checkBox = (checked) => checked ? '☑' : '☐';
  doc.text(`${checkBox(labRequest.is_pregnant)} Pregnant`, pageWidth / 2 + 7, conditionsY + 14);
  doc.text(`${checkBox(labRequest.is_fasting)} Fasting ${labRequest.fasting_hours ? `(${labRequest.fasting_hours}hrs)` : ''}`, pageWidth / 2 + 7, conditionsY + 20);
  doc.text(`${checkBox(labRequest.on_anticoagulants)} Anticoagulants ${labRequest.anticoagulant_drug ? `(${labRequest.anticoagulant_drug})` : ''}`, pageWidth / 2 + 7, conditionsY + 26);
  
  yPos += 35;
  
  // Lab Tests Section
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('LABORATORY TESTS REQUESTED', margin, yPos);
  yPos += 5;
  
  // Draw tests in a grid layout
  const testsPerRow = 2;
  const colWidth = (pageWidth - margin * 2) / testsPerRow;
  let currentCol = 0;
  let rowStartY = yPos;
  
  doc.setFontSize(8);
  
  const categories = labRequest.selected_tests || {};
  const categoryEntries = Object.entries(categories).filter(([_, tests]) => tests && tests.length > 0);
  
  for (const [categoryKey, tests] of categoryEntries) {
    // Check if we need a new page
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = 20;
      rowStartY = yPos;
      currentCol = 0;
    }
    
    const categoryName = categoryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    // Category box
    const boxX = margin + (currentCol * colWidth);
    const boxWidth = colWidth - 5;
    const boxHeight = 8 + (tests.length * 5);
    
    // Check if this category fits in current row
    if (rowStartY + boxHeight > pageHeight - 60 && currentCol === 0) {
      doc.addPage();
      yPos = 20;
      rowStartY = yPos;
    }
    
    doc.setFillColor(239, 246, 255);
    doc.roundedRect(boxX, rowStartY, boxWidth, boxHeight, 1, 1, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);
    doc.text(categoryName, boxX + 3, rowStartY + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    
    tests.forEach((test, idx) => {
      doc.text(`✓ ${test}`, boxX + 5, rowStartY + 10 + (idx * 5));
    });
    
    currentCol++;
    if (currentCol >= testsPerRow) {
      currentCol = 0;
      rowStartY += boxHeight + 3;
    }
    
    yPos = Math.max(yPos, rowStartY + boxHeight + 3);
  }
  
  // If we ended on a partial row, move to next row
  if (currentCol !== 0) {
    yPos = rowStartY + 20;
  }
  
  // Check if we need new page for notes and signature
  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = 20;
  }
  
  // Notes section
  if (labRequest.notes) {
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text('ADDITIONAL NOTES:', margin, yPos);
    yPos += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    const notesLines = doc.splitTextToSize(labRequest.notes, pageWidth - margin * 2);
    doc.text(notesLines, margin, yPos);
    yPos += notesLines.length * 4 + 5;
  }
  
  // Fasting Instructions
  if (labRequest.is_fasting) {
    yPos += 5;
    doc.setFillColor(254, 242, 242);
    doc.roundedRect(margin, yPos, pageWidth - margin * 2, 15, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(153, 27, 27);
    doc.text('⚠ FASTING INSTRUCTIONS:', margin + 5, yPos + 5);
    doc.setFont('helvetica', 'normal');
    doc.text(`Patient must fast for ${labRequest.fasting_hours || 9}-12 hours before specimen collection. Water is allowed.`, margin + 5, yPos + 11);
    yPos += 20;
  }
  
  // Signature section at bottom
  yPos = Math.max(yPos + 10, pageHeight - 40);
  
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, margin + 60, yPos);
  doc.line(pageWidth - margin - 60, yPos, pageWidth - margin, yPos);
  
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('Physician Signature', margin, yPos + 5);
  doc.text('Date', pageWidth - margin - 60, yPos + 5);
  
  // Footer
  addFooter(doc, labRequest.created_by_name || user?.full_name, labRequest.created_at);
  
  // Save
  const patientName = (labRequest.patient_name || 'Patient').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`Lab_Request_${labRequest.reference_number}_${patientName}_${dateStr}.pdf`);
};


/**
 * Generate a print-friendly Patient History Summary PDF
 * Includes all structured history sections for referral or patient handout
 */
export const generatePatientHistorySummaryPDF = async ({ patient, history, generatedBy, generatedByPhone }) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const marginLeft = 15;
  const marginRight = pageWidth - 15;
  const maxLineWidth = marginRight - marginLeft;

  let yPos = await addHeader(doc, 'PATIENT HISTORY SUMMARY', `Patient ID: ${patient.patient_id || ''}`);

  // Helper: check if we need a new page
  const checkNewPage = (needed = 20) => {
    if (yPos + needed > 270) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Helper: section heading
  const sectionHeading = (title) => {
    checkNewPage(25);
    doc.setFillColor(240, 245, 250);
    doc.rect(marginLeft, yPos - 5, maxLineWidth, 9, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(title, marginLeft + 2, yPos);
    yPos += 9;
  };

  // Helper: key-value line
  const kvLine = (label, value) => {
    checkNewPage();
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text(`${label}:`, marginLeft + 2, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    const valText = value || 'N/A';
    const lines = doc.splitTextToSize(String(valText), maxLineWidth - 45);
    doc.text(lines, marginLeft + 42, yPos);
    yPos += Math.max(lines.length * 4.5, 5.5);
  };

  // ========================
  // PATIENT DEMOGRAPHICS
  // ========================
  sectionHeading('PATIENT DEMOGRAPHICS');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(`${patient.first_name} ${patient.last_name}`, marginLeft + 2, yPos);
  yPos += 7;

  const col1X = marginLeft + 2;
  const col2X = 110;
  doc.setFontSize(9);

  const dob = patient.date_of_birth ? formatDate(patient.date_of_birth) : 'N/A';
  doc.setFont('helvetica', 'bold'); doc.setTextColor(80,80,80);
  doc.text('Date of Birth:', col1X, yPos);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(30,30,30);
  doc.text(dob, col1X + 28, yPos);

  doc.setFont('helvetica', 'bold'); doc.setTextColor(80,80,80);
  doc.text('Gender:', col2X, yPos);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(30,30,30);
  doc.text(patient.gender || 'N/A', col2X + 18, yPos);
  yPos += 5.5;

  if (patient.phone || patient.email) {
    doc.setFont('helvetica', 'bold'); doc.setTextColor(80,80,80);
    doc.text('Phone:', col1X, yPos);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(30,30,30);
    doc.text(patient.phone || 'N/A', col1X + 28, yPos);

    doc.setFont('helvetica', 'bold'); doc.setTextColor(80,80,80);
    doc.text('Email:', col2X, yPos);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(30,30,30);
    doc.text(patient.email || 'N/A', col2X + 18, yPos);
    yPos += 5.5;
  }

  if (patient.blood_type) {
    doc.setFont('helvetica', 'bold'); doc.setTextColor(80,80,80);
    doc.text('Blood Type:', col1X, yPos);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(30,30,30);
    doc.text(patient.blood_type, col1X + 28, yPos);
    yPos += 5.5;
  }
  yPos += 4;

  // ========================
  // ALLERGIES (highlighted)
  // ========================
  sectionHeading('ALLERGIES');
  const allergies = history?.allergies || [];
  if (allergies.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 139, 94);
    doc.text('NKDA (No Known Drug Allergies)', marginLeft + 2, yPos);
    yPos += 7;
  } else {
    const allergyRows = allergies.map(a => [
      a.allergen,
      (a.allergy_type || '').charAt(0).toUpperCase() + (a.allergy_type || '').slice(1),
      (a.severity || '').toUpperCase(),
      a.reaction || '-'
    ]);
    autoTable(doc, {
      startY: yPos,
      margin: { left: marginLeft },
      head: [['Allergen', 'Type', 'Severity', 'Reaction']],
      body: allergyRows,
      theme: 'grid',
      headStyles: { fillColor: [220, 50, 50], fontSize: 9, fontStyle: 'bold', textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        2: { fontStyle: 'bold', textColor: [200, 0, 0] }
      },
      tableWidth: maxLineWidth,
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  // ========================
  // PAST MEDICAL HISTORY
  // ========================
  sectionHeading('PAST MEDICAL HISTORY');
  const conditions = history?.past_medical_history || [];
  if (conditions.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(120,120,120);
    doc.text('No past medical history recorded.', marginLeft + 2, yPos);
    yPos += 7;
  } else {
    const condRows = conditions.map(c => [
      c.condition,
      (c.status || '').charAt(0).toUpperCase() + (c.status || '').slice(1),
      c.year_diagnosed || '-',
      c.notes || '-'
    ]);
    autoTable(doc, {
      startY: yPos,
      margin: { left: marginLeft },
      head: [['Condition', 'Status', 'Year Dx', 'Notes']],
      body: condRows,
      theme: 'grid',
      headStyles: { fillColor: [34, 139, 94], fontSize: 9, fontStyle: 'bold', textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9 },
      tableWidth: maxLineWidth,
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  // ========================
  // CURRENT MEDICATIONS
  // ========================
  sectionHeading('CURRENT MEDICATIONS');
  const meds = history?.medications || [];
  const currentMeds = meds.filter(m => m.is_current);
  if (currentMeds.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(120,120,120);
    doc.text('No current medications recorded.', marginLeft + 2, yPos);
    yPos += 7;
  } else {
    const medRows = currentMeds.map(m => [
      m.medication_name + (m.is_otc ? ' (OTC)' : '') + (m.is_supplement ? ' (Supp)' : ''),
      m.dose || '-',
      m.frequency || '-',
      m.route || '-',
      m.indication || '-'
    ]);
    autoTable(doc, {
      startY: yPos,
      margin: { left: marginLeft },
      head: [['Medication', 'Dose', 'Frequency', 'Route', 'Indication']],
      body: medRows,
      theme: 'grid',
      headStyles: { fillColor: [34, 139, 94], fontSize: 9, fontStyle: 'bold', textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9 },
      tableWidth: maxLineWidth,
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  // Past medications
  const pastMeds = meds.filter(m => !m.is_current);
  if (pastMeds.length > 0) {
    checkNewPage(15);
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(80,80,80);
    doc.text('Past Medications:', marginLeft + 2, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(60,60,60);
    pastMeds.forEach(m => {
      checkNewPage();
      const line = `• ${m.medication_name}${m.dose ? ` ${m.dose}` : ''}${m.frequency ? `, ${m.frequency}` : ''}${m.end_date ? ` (stopped: ${m.end_date})` : ''}`;
      doc.text(doc.splitTextToSize(line, maxLineWidth - 4), marginLeft + 4, yPos);
      yPos += 5;
    });
    yPos += 3;
  }

  // ========================
  // FAMILY HISTORY
  // ========================
  sectionHeading('FAMILY HISTORY');
  const family = history?.family_history || [];
  if (family.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(120,120,120);
    doc.text('No family history recorded.', marginLeft + 2, yPos);
    yPos += 7;
  } else {
    const famRows = family.map(f => [
      f.relative,
      f.condition,
      f.age_diagnosed ? `Age ${f.age_diagnosed}` : '-',
      f.is_alive === true ? 'Living' : f.is_alive === false ? 'Deceased' : '-',
      f.notes || '-'
    ]);
    autoTable(doc, {
      startY: yPos,
      margin: { left: marginLeft },
      head: [['Relative', 'Condition', 'Age at Dx', 'Status', 'Notes']],
      body: famRows,
      theme: 'grid',
      headStyles: { fillColor: [34, 139, 94], fontSize: 9, fontStyle: 'bold', textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9 },
      tableWidth: maxLineWidth,
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  // ========================
  // SURGICAL HISTORY
  // ========================
  sectionHeading('SURGICAL HISTORY');
  const surgeries = history?.surgical_history || [];
  if (surgeries.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(120,120,120);
    doc.text('No surgical history recorded.', marginLeft + 2, yPos);
    yPos += 7;
  } else {
    const surgRows = surgeries.map(s => [
      s.procedure,
      s.body_site || '-',
      s.year || '-',
      s.hospital || '-',
      s.had_complications ? `Yes: ${s.complications || 'Details N/A'}` : 'None'
    ]);
    autoTable(doc, {
      startY: yPos,
      margin: { left: marginLeft },
      head: [['Procedure', 'Body Site', 'Year', 'Hospital', 'Complications']],
      body: surgRows,
      theme: 'grid',
      headStyles: { fillColor: [34, 139, 94], fontSize: 9, fontStyle: 'bold', textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9 },
      tableWidth: maxLineWidth,
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  // ========================
  // SOCIAL HISTORY
  // ========================
  sectionHeading('SOCIAL HISTORY');
  const social = history?.social_history;
  if (!social) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(120,120,120);
    doc.text('No social history recorded.', marginLeft + 2, yPos);
    yPos += 7;
  } else {
    // Smoking
    let smokingText = social.smoking_status === 'never' ? 'Never smoker'
      : social.smoking_status === 'former' ? 'Former smoker' : 'Current smoker';
    if ((social.smoking_status === 'current' || social.smoking_status === 'former') && social.packs_per_day) {
      smokingText += ` — ${social.packs_per_day} ppd`;
      if (social.years_smoked) smokingText += ` x ${social.years_smoked} yrs`;
      if (social.pack_years || (social.packs_per_day && social.years_smoked)) {
        const py = social.pack_years || (social.packs_per_day * social.years_smoked);
        smokingText += ` (${py.toFixed ? py.toFixed(1) : py} pack-years)`;
      }
    }
    kvLine('Smoking', smokingText);

    // Alcohol
    let alcoholText = social.alcohol_status === 'none' ? 'None' : social.alcohol_status.charAt(0).toUpperCase() + social.alcohol_status.slice(1);
    if (social.alcohol_status !== 'none' && social.drinks_per_week) {
      alcoholText += ` — ${social.drinks_per_week} drinks/week`;
    }
    kvLine('Alcohol', alcoholText);

    // Drug use
    if (social.drug_use !== 'none') {
      kvLine('Recreational Drugs', `${social.drug_use === 'past' ? 'Past use' : 'Current use'}${social.drug_details ? ` — ${social.drug_details}` : ''}`);
    }

    if (social.occupation) kvLine('Occupation', social.occupation);
    if (social.marital_status) kvLine('Marital Status', social.marital_status.charAt(0).toUpperCase() + social.marital_status.slice(1));
    if (social.living_situation) kvLine('Living Situation', social.living_situation);
    yPos += 3;
  }

  // ========================
  // SIGNATURE LINE
  // ========================
  checkNewPage(35);
  yPos += 5;
  doc.setDrawColor(180, 180, 180);
  doc.line(marginLeft, yPos, marginLeft + 70, yPos);
  yPos += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text(generatedBy || 'Physician', marginLeft, yPos);
  yPos += 4;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  const histPhysicianPhone = generatedByPhone || getClinicianPhone(generatedBy);
  if (histPhysicianPhone) {
    doc.text(histPhysicianPhone, marginLeft, yPos);
    yPos += 4;
  }
  doc.text(CLINIC_NAME, marginLeft, yPos);
  yPos += 4;
  doc.text(`Contact: ${BUSINESS_PHONES_INLINE}`, marginLeft, yPos);
  yPos += 4;
  doc.text(`Date: ${formatDate(new Date())}`, marginLeft, yPos);

  // Footer on all pages
  addFooter(doc, generatedBy, new Date());

  // Save
  const patientName = `${patient.first_name}_${patient.last_name}`.replace(/[^a-zA-Z0-9_]/g, '');
  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`Patient_History_${patientName}_${dateStr}.pdf`);
};


// =============================================================================
// ENCOUNTER DOCUMENT GENERATORS (Phase 3)
// =============================================================================

/**
 * Generate a Medical Report PDF from encounter data
 */
export const generateEncounterReport = (data) => {
  const { encounter, patient, provider } = data;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFillColor(16, 95, 67);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('MEDICAL REPORT', pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(CLINIC_NAME, pageWidth / 2, 19, { align: 'center' });
  doc.text(`${CLINIC_ADDRESS} | ${CLINIC_EMAIL}`, pageWidth / 2, 24, { align: 'center' });
  doc.setFontSize(8);
  doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, pageWidth / 2, 29, { align: 'center' });

  let y = 42;
  doc.setTextColor(30, 30, 30);

  // Patient Info
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('PATIENT INFORMATION', 14, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const patientInfo = [
    `Name: ${patient.first_name} ${patient.last_name}`,
    `DOB: ${patient.date_of_birth || 'N/A'}  |  Gender: ${patient.gender || 'N/A'}`,
    `ID: ${patient.patient_id || 'N/A'}  |  Phone: ${patient.phone || 'N/A'}`,
  ];
  patientInfo.forEach(line => { doc.text(line, 14, y); y += 5; });
  y += 4;

  // Encounter Info
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('ENCOUNTER DETAILS', 14, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Date: ${encounter.encounter_date}  |  Type: ${encounter.encounter_type}  |  Provider: ${provider.name}, ${provider.title}`, 14, y);
  y += 5;
  if (encounter.chief_complaint) { doc.text(`Chief Complaint: ${encounter.chief_complaint}`, 14, y); y += 5; }
  y += 3;

  // Vital Signs
  const vs = encounter.vital_signs || {};
  if (vs.blood_pressure_systolic || vs.heart_rate || vs.temperature) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('VITAL SIGNS', 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const vitals = [];
    if (vs.blood_pressure_systolic) vitals.push(`BP: ${vs.blood_pressure_systolic}/${vs.blood_pressure_diastolic} mmHg`);
    if (vs.heart_rate) vitals.push(`HR: ${vs.heart_rate} bpm`);
    if (vs.temperature) vitals.push(`Temp: ${vs.temperature} C`);
    if (vs.respiratory_rate) vitals.push(`RR: ${vs.respiratory_rate}/min`);
    if (vs.oxygen_saturation) vitals.push(`SpO2: ${vs.oxygen_saturation}%`);
    if (vs.weight) vitals.push(`Weight: ${vs.weight} kg`);
    if (vs.height) vitals.push(`Height: ${vs.height} cm`);
    if (vs.bmi) vitals.push(`BMI: ${vs.bmi}`);
    doc.text(vitals.join('  |  '), 14, y);
    y += 8;
  }

  // SOAP sections
  const sections = [
    { title: 'SUBJECTIVE', text: encounter.subjective },
    { title: 'OBJECTIVE', text: encounter.objective },
    { title: 'PHYSICAL EXAMINATION', text: encounter.physical_exam },
    { title: 'ASSESSMENT', text: encounter.assessment },
    { title: 'PLAN', text: encounter.plan },
    { title: 'MANAGEMENT PLAN', text: encounter.management_plan },
    { title: 'FOLLOW-UP', text: encounter.follow_up },
  ];

  sections.forEach(({ title, text }) => {
    if (!text) return;
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(title, 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(text, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 4 + 4;
  });

  // Diagnoses
  if (encounter.diagnoses?.length && encounter.diagnoses[0].description) {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DIAGNOSES', 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    encounter.diagnoses.filter(d => d.description).forEach(d => {
      doc.text(`${d.type === 'primary' ? '[Primary]' : '[Secondary]'} ${d.code ? d.code + ' - ' : ''}${d.description}`, 14, y);
      y += 5;
    });
    y += 3;
  }

  // Additional notes
  if (data.additional_notes) {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('ADDITIONAL NOTES', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(data.additional_notes, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 4 + 4;
  }

  // Signature
  if (y > 250) { doc.addPage(); y = 20; }
  y += 10;
  doc.setDrawColor(100, 100, 100);
  doc.line(14, y, 80, y);
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`${provider.name}, ${provider.title}`, 14, y);
  y += 4;
  const encReportPhone = getClinicianPhone(provider.name);
  if (encReportPhone) {
    doc.setFont('helvetica', 'normal');
    doc.text(`Direct: ${encReportPhone}`, 14, y);
    y += 4;
  }
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${encounter.encounter_date}`, 14, y);

  const pName = `${patient.first_name}_${patient.last_name}`.replace(/[^a-zA-Z0-9_]/g, '');
  doc.save(`Medical_Report_${pName}_${encounter.encounter_date}.pdf`);
};

/**
 * Generate a Medical Certificate PDF
 */
export const generateMedicalCertificate = (data) => {
  const { encounter, patient, provider } = data;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFillColor(16, 95, 67);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('MEDICAL CERTIFICATE', pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(CLINIC_NAME, pageWidth / 2, 19, { align: 'center' });
  doc.text(`${CLINIC_ADDRESS} | ${CLINIC_EMAIL}`, pageWidth / 2, 24, { align: 'center' });
  doc.setFontSize(8);
  doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, pageWidth / 2, 29, { align: 'center' });

  let y = 48;
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  doc.text('This is to certify that:', 14, y); y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(`${patient.first_name} ${patient.last_name}`, 14, y); y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Date of Birth: ${patient.date_of_birth || 'N/A'}`, 14, y); y += 5;
  doc.text(`Gender: ${patient.gender || 'N/A'}`, 14, y); y += 10;

  doc.text('Was examined on this date and found to require medical leave.', 14, y); y += 10;

  if (data.certificate_reason) {
    doc.setFont('helvetica', 'bold');
    doc.text('Reason:', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(data.certificate_reason, 50, y);
    y += 8;
  }

  if (encounter.diagnoses?.length && encounter.diagnoses[0].description) {
    doc.setFont('helvetica', 'bold');
    doc.text('Diagnosis:', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(encounter.diagnoses[0].description, 50, y);
    y += 8;
  }

  if (data.start_date || data.end_date) {
    doc.setFont('helvetica', 'bold');
    doc.text('Period:', 14, y);
    doc.setFont('helvetica', 'normal');
    const period = [data.start_date, data.end_date].filter(Boolean).join(' to ');
    doc.text(period, 50, y);
    y += 8;
  }

  if (data.additional_notes) {
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 14, y); y += 5;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(data.additional_notes, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 5;
  }

  // Signature
  y += 25;
  doc.setDrawColor(100, 100, 100);
  doc.line(14, y, 80, y);
  y += 5;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${provider.name}`, 14, y); y += 5;
  doc.setFont('helvetica', 'normal');
  const certPhone = getClinicianPhone(provider.name);
  if (certPhone) {
    doc.setFontSize(9);
    doc.text(`Direct: ${certPhone}`, 14, y); y += 5;
    doc.setFontSize(10);
  }
  doc.text(`${provider.title}`, 14, y); y += 5;
  doc.text(`Date: ${encounter.encounter_date}`, 14, y);

  const pName = `${patient.first_name}_${patient.last_name}`.replace(/[^a-zA-Z0-9_]/g, '');
  doc.save(`Medical_Certificate_${pName}_${encounter.encounter_date}.pdf`);
};

/**
 * Generate a Referral Letter PDF
 */
export const generateReferralLetter = (data) => {
  const { encounter, patient, provider } = data;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFillColor(16, 95, 67);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('REFERRAL LETTER', pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(CLINIC_NAME, pageWidth / 2, 19, { align: 'center' });
  doc.text(`${CLINIC_ADDRESS} | ${CLINIC_EMAIL}`, pageWidth / 2, 24, { align: 'center' });
  doc.setFontSize(8);
  doc.text(`Tel: ${BUSINESS_PHONES_INLINE}`, pageWidth / 2, 29, { align: 'center' });

  let y = 42;
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(10);

  // Date and To
  doc.text(`Date: ${encounter.encounter_date}`, 14, y); y += 8;
  if (data.referred_to) {
    doc.setFont('helvetica', 'bold');
    doc.text('To:', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(data.referred_to, 25, y); y += 5;
  }
  if (data.specialty) {
    doc.text(`Specialty: ${data.specialty}`, 14, y); y += 5;
  }
  if (data.urgency) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Urgency: ${data.urgency.toUpperCase()}`, 14, y);
    doc.setFont('helvetica', 'normal');
    y += 5;
  }
  y += 5;

  // RE line
  doc.setFont('helvetica', 'bold');
  doc.text(`RE: ${patient.first_name} ${patient.last_name}`, 14, y); y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text(`DOB: ${patient.date_of_birth || 'N/A'}  |  Gender: ${patient.gender || 'N/A'}  |  Phone: ${patient.phone || 'N/A'}`, 14, y);
  y += 10;

  // Body
  doc.text('Dear Colleague,', 14, y); y += 8;
  let bodyText = `I am writing to refer the above patient for your expert opinion and management. `;
  if (encounter.chief_complaint) bodyText += `The patient presented with ${encounter.chief_complaint.toLowerCase()}. `;

  const bodyLines = doc.splitTextToSize(bodyText, pageWidth - 28);
  doc.text(bodyLines, 14, y);
  y += bodyLines.length * 5 + 5;

  // Clinical Details
  const clinicalSections = [
    { title: 'History', text: encounter.subjective },
    { title: 'Examination Findings', text: encounter.objective || encounter.physical_exam },
    { title: 'Assessment', text: encounter.assessment },
    { title: 'Current Management', text: encounter.plan || encounter.management_plan },
  ];

  clinicalSections.forEach(({ title, text }) => {
    if (!text) return;
    if (y > 255) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.text(`${title}:`, 14, y); y += 5;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(text, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 4.5 + 4;
  });

  // Diagnoses
  if (encounter.diagnoses?.length && encounter.diagnoses[0].description) {
    if (y > 255) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.text('Diagnosis:', 14, y); y += 5;
    doc.setFont('helvetica', 'normal');
    encounter.diagnoses.filter(d => d.description).forEach(d => {
      doc.text(`- ${d.code ? d.code + ': ' : ''}${d.description}`, 14, y); y += 5;
    });
    y += 3;
  }

  if (data.additional_notes) {
    if (y > 255) { doc.addPage(); y = 20; }
    const lines = doc.splitTextToSize(data.additional_notes, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 5 + 5;
  }

  // Closing
  if (y > 240) { doc.addPage(); y = 20; }
  y += 5;
  doc.text('Thank you for seeing this patient. I would appreciate your assessment and management recommendations.', 14, y);
  y += 10;
  doc.text('Kind regards,', 14, y);
  y += 15;
  doc.setDrawColor(100, 100, 100);
  doc.line(14, y, 80, y); y += 5;
  doc.setFont('helvetica', 'bold');
  doc.text(`${provider.name}`, 14, y); y += 5;
  const refPhone = getClinicianPhone(provider.name);
  if (refPhone) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Direct: ${refPhone}`, 14, y); y += 5;
    doc.setFontSize(10);
  }
  doc.setFont('helvetica', 'normal');
  doc.text(`${provider.title}`, 14, y);

  const pName = `${patient.first_name}_${patient.last_name}`.replace(/[^a-zA-Z0-9_]/g, '');
  doc.save(`Referral_Letter_${pName}_${encounter.encounter_date}.pdf`);
};
